import React from "react";
import { connect } from "react-redux";
import moment from "moment";
import { Button } from "../../../Button";
import { Select } from "../../../Input";
import toastMessage from "../../../../utils/toastMessage";
import fetchBloodDonation from "../../../../utils/queries/bloodDonationQuery";
import centerQuery from "../../../../utils/queries/bloodCenterQuery";
import centerSiteQuery from "../../../../utils/queries/bloodCenterSiteQuery";
import { DashboardHeader } from "../../../Header";

const BLOOD_GROUPS = ["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"];

const MONTHS = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

const YEARS = Array.from({ length: 10 }, (_, i) => {
  const y = new Date().getFullYear() - i;
  return { label: String(y), value: String(y) };
});

class DailyDonationsReport extends React.Component {
  state = {
    month: moment().format("MM"),
    year: moment().format("YYYY"),
    center: null,
    centerSite: null,
    centers: [],
    centerSites: [],
    isLoading: false,
    rows: [],
    totals: null,
    searched: false,
  };

  componentDidMount() {
    this.getCenters();
  }

  getCenters = async () => {
    const data = await centerQuery(this.props.defaultLanguage, {
      type: "dropdown",
    });
    this.setState({ centers: data || [] });
  };

  getCenterSites = async (centerId) => {
    const data = await centerSiteQuery(this.props.defaultLanguage, {
      type: "dropdown",
      center: centerId,
    });
    this.setState({ centerSites: data || [] });
  };

  onChangeCenter = (center) => {
    this.setState({ center, centerSite: null, centerSites: [] });
    if (center?.value) this.getCenterSites(center.value);
  };

  fetchReport = async () => {
    const { month, year, center, centerSite } = this.state;
    if (!month || !year) return;

    this.setState({ isLoading: true, rows: [], totals: null, searched: true });

    const startDate = moment(`${year}-${month}-01`).startOf("month").format("YYYY-MM-DD");
    const endDate   = moment(`${year}-${month}-01`).endOf("month").format("YYYY-MM-DD");

    try {
      const params = { startDate, endDate, limit: 10000, page: 1 };
      if (center?.value)     params.center     = center.value;
      if (centerSite?.value) params.centerSite = centerSite.value;

      const res = await fetchBloodDonation(this.props.defaultLanguage, params);
      const donations = Array.isArray(res) ? res : res?.data || [];

      const dayMap = {};
      donations.forEach((d) => {
        const key = moment(d.collectionDate).format("YYYY-MM-DD");
        if (!dayMap[key]) {
          dayMap[key] = { date: key, nd: 0, id: 0, rd: 0, total: 0 };
          BLOOD_GROUPS.forEach((bg) => { dayMap[key][bg] = 0; });
        }
        const cat = (d.donorCategory || "").toLowerCase();
        if (cat === "new")       dayMap[key].nd++;
        else if (cat === "irregular") dayMap[key].id++;
        else if (cat === "regular")   dayMap[key].rd++;
        if (d.bloodGroup && dayMap[key][d.bloodGroup] !== undefined) dayMap[key][d.bloodGroup]++;
        dayMap[key].total++;
      });

      const daysInMonth = moment(`${year}-${month}-01`).daysInMonth();
      const rows = [];
      const totals = { nd: 0, id: 0, rd: 0, total: 0 };
      BLOOD_GROUPS.forEach((bg) => { totals[bg] = 0; });

      for (let d = 1; d <= daysInMonth; d++) {
        const dayStr = `${year}-${month}-${String(d).padStart(2, "0")}`;
        const empty = { date: dayStr, nd: 0, id: 0, rd: 0, total: 0 };
        BLOOD_GROUPS.forEach((bg) => { empty[bg] = 0; });
        const row = dayMap[dayStr] || empty;
        rows.push(row);
        totals.nd    += row.nd;
        totals.id    += row.id;
        totals.rd    += row.rd;
        totals.total += row.total;
        BLOOD_GROUPS.forEach((bg) => { totals[bg] += row[bg]; });
      }

      this.setState({ rows, totals, isLoading: false });
    } catch (error) {
      toastMessage("error", error);
      this.setState({ isLoading: false });
    }
  };

  render() {
    const {
      isLoading, rows, totals,
      month, year, center, centerSite,
      centers, centerSites, searched,
    } = this.state;

    const monthLabel = MONTHS.find((m) => m.value === month)?.label || "";
    const titleLabel = `${monthLabel} ${year}`;

    return (
      <div className="card-dashboard-content">
        <style>{PRINT_CSS}</style>
        <div className="card-body">
          <div style={{ marginBottom: 16 }}>
            <DashboardHeader title="Daily Donations Report" />
          </div>

          {/* ── Filter bar (single line) ──────────────────────────── */}
          <div
            className="no-print"
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 16,
              marginBottom: 24,
              padding: "12px 20px",
              background: "var(--primary-color-5)",
              borderRadius: "var(--border-radius)",
              border: "1px solid var(--text-color-10)",
            }}
          >
            {/* Month */}
            <div style={inlineField}>
              <span style={filterLabel}>Month</span>
              <select
                value={month}
                onChange={(e) => this.setState({ month: e.target.value })}
                style={{ ...nativeSel, minWidth: 140 }}
              >
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            {/* Year */}
            <div style={inlineField}>
              <span style={filterLabel}>Year</span>
              <select
                value={year}
                onChange={(e) => this.setState({ year: e.target.value })}
                style={{ ...nativeSel, minWidth: 88 }}
              >
                {YEARS.map((y) => (
                  <option key={y.value} value={y.value}>{y.label}</option>
                ))}
              </select>
            </div>

            <div style={{ width: 1, height: 32, background: "var(--border-color)" }} />

            {/* Center */}
            <div style={inlineField}>
              <span style={filterLabel}>Center</span>
              <div style={{ minWidth: 220 }}>
                <Select
                  options={centers}
                  value={center}
                  onChange={this.onChangeCenter}
                  styles={{ margin: 0, minHeight: 0 }}
                />
              </div>
            </div>

            {/* Site */}
            <div style={inlineField}>
              <span style={filterLabel}>Site</span>
              <div style={{ minWidth: 220 }}>
                <Select
                  options={centerSites}
                  value={centerSite}
                  onChange={(v) => this.setState({ centerSite: v })}
                  disabled={!center}
                  styles={{ margin: 0, minHeight: 0 }}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <Button
                text="Search"
                className="btn-primary"
                onPress={this.fetchReport}
                isSubmitting={isLoading}
              />
              {rows.length > 0 && (
                <Button
                  text="Print"
                  className="btn-default border"
                  onPress={() => window.print()}
                />
              )}
            </div>
          </div>

          {/* ── Body (wrapper used for print isolation) ────────────── */}
          <div id="daily-donations-wrapper">

          {/* ── Print-only header ──────────────────────────────────── */}
          <div className="print-only" style={{ display: "none", marginBottom: 16, paddingBottom: 10, borderBottom: "2px solid #ac0207" }}>
            <h3 style={{ margin: 0, fontSize: 15, color: "#ac0207" }}>
              Daily Donations Report — {titleLabel}
            </h3>
            {center && (
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "#555" }}>
                Center: <strong>{center.label}</strong>
                {centerSite ? ` / Site: ${centerSite.label}` : ""}
              </p>
            )}
            <p style={{ margin: "2px 0 0", fontSize: 11, color: "#888" }}>
              Printed: {moment().format("DD/MM/YYYY HH:mm")}
            </p>
          </div>

          {/* ── Body ──────────────────────────────────────────────── */}
          <div id="daily-donations-print-area">
            {isLoading ? (
              <div style={{ textAlign: "center", padding: "3rem", color: "var(--text-color-60)", fontSize: "var(--font-size-sm)" }}>
                Loading…
              </div>
            ) : rows.length > 0 ? (
              <div className="table-responsive" style={{ overflowX: "auto" }}>
                <table
                  className="table"
                  style={{ minWidth: 960, fontSize: "var(--font-size-sm)", borderCollapse: "collapse", width: "100%" }}
                >
                  <thead>
                    {/* Row 1: group headers */}
                    <tr style={{ backgroundColor: "var(--primary-color)", color: "#fff" }}>
                      <th rowSpan={2} style={{ ...TH, textAlign: "left", paddingLeft: 14, verticalAlign: "middle", width: 110, borderRight: SEP_WHITE }}>
                        Date
                      </th>
                      <th rowSpan={2} style={{ ...TH, verticalAlign: "middle", width: 58, borderRight: SEP_WHITE }}>
                        Day
                      </th>
                      <th colSpan={3} style={{ ...TH, borderRight: SEP_WHITE }}>
                        Donor Category
                      </th>
                      <th colSpan={8} style={{ ...TH, borderRight: SEP_WHITE }}>
                        Blood Group
                      </th>
                      <th rowSpan={2} style={{ ...TH, verticalAlign: "middle", minWidth: 70, background: "var(--dark-primary-color)", color: "#fff" }}>
                        Total
                      </th>
                    </tr>

                    {/* Row 2: column names */}
                    <tr style={{ backgroundColor: "var(--primary-color-60)", color: "#fff" }}>
                      <th style={{ ...TH, fontSize: 11 }}>ND</th>
                      <th style={{ ...TH, fontSize: 11 }}>ID</th>
                      <th style={{ ...TH, fontSize: 11, borderRight: SEP_WHITE }}>RD</th>
                      {BLOOD_GROUPS.map((bg, i) => (
                        <th key={bg} style={{ ...TH, fontSize: 11, borderRight: i === BLOOD_GROUPS.length - 1 ? SEP_WHITE : undefined }}>
                          {bg}
                        </th>
                      ))}
                    </tr>

                    {/* Row 3: sub-label row */}
                    <tr style={{ background: "#f0f0f0", fontSize: "var(--font-size-xsm)", color: "#888", fontStyle: "italic" }}>
                      <td colSpan={2} style={{ padding: "3px 14px", border: 0 }} />
                      <td style={SUB_TD}>New Donor</td>
                      <td style={SUB_TD}>Irregular</td>
                      <td style={{ ...SUB_TD, borderRight: "1px solid #ddd" }}>Regular</td>
                      {BLOOD_GROUPS.map((bg, i) => (
                        <td key={bg} style={{ ...SUB_TD, borderRight: i === BLOOD_GROUPS.length - 1 ? "1px solid #ddd" : undefined }} />
                      ))}
                      <td style={{ padding: "3px 10px", border: 0 }} />
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((row, i) => {
                      const dow = moment(row.date).day();
                      const isWeekend = dow === 0 || dow === 6;
                      const rowBg = isWeekend ? "#fffbf0" : i % 2 === 0 ? "#fff" : "var(--primary-color-5)";

                      return (
                        <tr key={row.date} style={{ backgroundColor: rowBg }}>
                          <td style={{ ...TD, textAlign: "left", paddingLeft: 14, fontWeight: 600, borderRight: "1px solid #eee" }}>
                            {moment(row.date).format("DD/MM/YYYY")}
                          </td>
                          <td style={{ ...TD, fontWeight: isWeekend ? 700 : 400, color: isWeekend ? "var(--secondary-color)" : "var(--text-color-60)", borderRight: "1px solid #eee" }}>
                            {moment(row.date).format("ddd")}
                          </td>
                          <td style={TD}>{row.nd}</td>
                          <td style={TD}>{row.id}</td>
                          <td style={{ ...TD, borderRight: "1px solid #eee" }}>{row.rd}</td>
                          {BLOOD_GROUPS.map((bg, idx) => (
                            <td key={bg} style={{ ...TD, borderRight: idx === BLOOD_GROUPS.length - 1 ? "1px solid #eee" : undefined }}>
                              {row[bg]}
                            </td>
                          ))}
                          <td style={{ ...TD, fontWeight: 700, color: row.total > 0 ? "var(--primary-color)" : "var(--text-color-60)" }}>
                            {row.total}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  <tfoot>
                    <tr style={{ backgroundColor: "var(--primary-color)", color: "#fff", fontWeight: 700 }}>
                      <td colSpan={2} style={{ ...TD, color: "#fff", textAlign: "left", paddingLeft: 14, letterSpacing: "0.04em", borderRight: "1px solid rgba(255,255,255,0.2)" }}>
                        TOTAL — {titleLabel}
                      </td>
                      <td style={{ ...TD, color: "#fff" }}>{totals?.nd}</td>
                      <td style={{ ...TD, color: "#fff" }}>{totals?.id}</td>
                      <td style={{ ...TD, color: "#fff", borderRight: "1px solid rgba(255,255,255,0.2)" }}>{totals?.rd}</td>
                      {BLOOD_GROUPS.map((bg, i) => (
                        <td key={bg} style={{ ...TD, color: "#fff", borderRight: i === BLOOD_GROUPS.length - 1 ? "1px solid rgba(255,255,255,0.2)" : undefined }}>
                          {totals?.[bg]}
                        </td>
                      ))}
                      <td style={{ ...TD, color: "#fff", fontWeight: 700, background: "var(--dark-primary-color)" }}>
                        {totals?.total}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              searched && !isLoading && (
                <div style={{ textAlign: "center", padding: "3rem", color: "var(--text-color-60)", border: "1px dashed var(--border-color)", borderRadius: "var(--border-radius)", fontSize: "var(--font-size-sm)" }}>
                  No donations found for {titleLabel}.
                </div>
              )
            )}
          </div>
          </div>{/* end #daily-donations-wrapper */}
        </div>
      </div>
    );
  }
}

/* ── Shared style objects ──────────────────────────────────── */

const inlineField = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const filterLabel = {
  fontSize: 13,
  fontWeight: 600,
  color: "var(--text-color-60)",
  whiteSpace: "nowrap",
};

const nativeSel = {
  height: 38,
  border: "2px solid var(--text-color-10)",
  borderRadius: "var(--border-radius)",
  backgroundColor: "var(--light-color)",
  padding: "0 10px",
  fontSize: "var(--font-size-sm)",
  color: "var(--text-color)",
  cursor: "pointer",
  outline: "none",
  appearance: "auto",
  WebkitAppearance: "auto",
  boxShadow: "none",
};

const SEP_WHITE = "1px solid rgba(255,255,255,0.2)";

const TH = {
  padding: "9px 10px",
  textAlign: "center",
  fontWeight: 600,
  whiteSpace: "nowrap",
  border: 0,
};

const SUB_TD = {
  padding: "3px 10px",
  textAlign: "center",
  border: 0,
};

const TD = {
  padding: "8px 10px",
  textAlign: "center",
  verticalAlign: "middle",
  border: "1px solid var(--border-color)",
  color: "var(--text-color)",
};

/* ── Print CSS ─────────────────────────────────────────────── */

const PRINT_CSS = `
  @media print {
    * { visibility: hidden !important; }
    #daily-donations-wrapper,
    #daily-donations-wrapper * { visibility: visible !important; }

    #daily-donations-wrapper {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      padding: 16px !important;
    }

    @page { size: A4 landscape; margin: 1cm; }

    table {
      font-size: 10px !important;
      width: 100% !important;
      border-collapse: collapse !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    table th, table td { padding: 4px 6px !important; }

    .table-responsive { overflow: visible !important; }
    .no-print { display: none !important; }
    .print-only { display: block !important; }
  }
`;

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return { defaultLanguage };
};

export default connect(mapStateToProps)(DailyDonationsReport);
